import { useEffect, useState } from "react";
import dayjs from "dayjs";
import ExcelJS from "exceljs";
import {
  Row,
  Col,
  Typography,
  Card,
  Select,
  Button,
  Collapse,
  Table,
  Tag,
  message,
  Modal,
  Form,
  Input,
  DatePicker,
  InputNumber,
  Divider,
  Checkbox,
} from "antd";
import {
  PlusOutlined,
  ReloadOutlined,
  ClearOutlined,
  DollarOutlined,
  TeamOutlined,
  FileTextOutlined,
  CalendarOutlined,
  BarChartOutlined,
  DownOutlined,
  InboxOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  FileExcelOutlined,
} from "@ant-design/icons";
import LayoutPage from "../../layouts/Main";
import ExpensesService from "../../utils/api/financial/expenses";
import PaymentCalendar from "../../components/Financial/PaymentCalendar";
import PaymentDetailsModal from "../../components/Financial/PaymentDetailsModal";
import "../../scss/expensesStyle.scss";

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

const createEmptyExpenseSummary = () => ({
  grand_total: 0,
  summary: {
    supplier: { MXN: { total: 0, count: 0 }, USD: { total: 0, count: 0 }, total: 0, count: 0 },
    tax: { total: 0, count: 0 },
    fixed: { total: 0, count: 0 },
    variable: { MXN: { total: 0, count: 0 }, USD: { total: 0, count: 0 }, total: 0, count: 0 },
  },
});

const personTypes = [
  { value: 1, label: "Persona Moral" },
  { value: 2, label: "Persona Fi­sica" },
];

const statusOptions = [
  { value: "PENDIENTE", label: "PENDIENTE" },
  { value: "CANCELADO", label: "CANCELADO" },
  { value: "PAGADO", label: "PAGADO" },
];

const validacionOptions = [
  { value: "COMPLETADO", label: "COMPLETADO" },
  { value: "INCOMPLETO", label: "INCOMPLETO" },
  { value: "S/N", label: "S/N" },
];

const expenseTypes = [
  { value: 1, label: "Proveedor" },
  { value: 2, label: "Impuesto" },
  { value: 3, label: "Fijo" },
  { value: 4, label: "Variable" },
];

const currencyOptions = [
  { value: "MXN", label: "MXN" },
  { value: "USD", label: "USD" },
];

const statusPayOptions = [
  { value: false, label: "PENDIENTE" },
  { value: true, label: "PAGADO" },
];

const typeTaxToModal = [
  { value: "8%", label: "8%" },
  { value: "16%", label: "16%" },
  { value: "OTRO", label: "Otro" },
];

const parseTaxRate = (value) => {
  if (value === null || value === undefined || value === "") {
    return 0;
  }

  if (typeof value === "string") {
    const numericValue = Number.parseFloat(value.replace("%", "").replace(",", "."));
    return Number.isFinite(numericValue) ? numericValue / 100 : 0;
  }

  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return 0;
  }

  return numericValue > 1 ? numericValue / 100 : numericValue;
};

const calculateDueDate = (receivedDate, termDays) => {
  if (!receivedDate || termDays === null || termDays === undefined || termDays === "") {
    return null;
  }

  const baseDate = dayjs(receivedDate);
  const days = Number(termDays);

  if (!baseDate.isValid() || !Number.isFinite(days)) {
    return null;
  }

  return baseDate.add(days, "day");
};

const calculateEstimatedPaymentDate = (dueDate) => {
  if (!dueDate) {
    return null;
  }

  const baseDate = dayjs(dueDate);

  if (!baseDate.isValid()) {
    return null;
  }

  const fridayIndex = 5;
  const currentDayIndex = baseDate.day();
  const daysUntilFriday = (fridayIndex - currentDayIndex + 7) % 7;

  return daysUntilFriday === 0 ? baseDate : baseDate.add(daysUntilFriday, "day");
};

const getExpenseReferenceDate = (expense) => {
  const rawDate =
    expense?.payment_date ||
    expense?.due_date ||
    expense?.received_date ||
    expense?.created_at ||
    null;

  return rawDate ? dayjs(rawDate) : null;
};

const isDateInRange = (dateValue, startDate, endDate) => {
  if (!dateValue) {
    return false;
  }

  const normalizedDate = dayjs(dateValue);
  if (!normalizedDate.isValid()) {
    return false;
  }

  const start = startDate ? dayjs(startDate).startOf("day") : null;
  const end = endDate ? dayjs(endDate).endOf("day") : null;

  if (start && normalizedDate.isBefore(start)) {
    return false;
  }

  if (end && normalizedDate.isAfter(end)) {
    return false;
  }

  return true;
};

const isMonthInRange = (dateValue, startMonth, endMonth) => {
  if (!dateValue) {
    return false;
  }

  const normalizedDate = dayjs(dateValue);
  if (!normalizedDate.isValid()) {
    return false;
  }

  if (startMonth && normalizedDate.month() + 1 < startMonth) {
    return false;
  }

  if (endMonth && normalizedDate.month() + 1 > endMonth) {
    return false;
  }

  return true;
};

const buildExpenseSummary = (expenses = []) => {
  const summary = createEmptyExpenseSummary();

  expenses.forEach((expense) => {
    const type = expense?.type;
    const amount = Number(expense?.amount) || 0;

    if (summary.summary[type]) {
      summary.summary[type].total += amount;
      summary.summary[type].count += 1;
    }

    summary.grand_total += amount;
  });

  return summary;
};

const formatMoney = (value) =>
  `$ ${Number(value || 0).toLocaleString("es-MX", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const getPendingRows = (rows = []) =>
  rows.filter((row) => {
    const status = row?.status;
    return status === "Pendiente" || status === "PENDIENTE" || status === "pending";
  });

const renderPendingFooter = (rows = []) => {
  const mxnRows = rows.filter((r) => (r.currency || "MXN") === "MXN");
  const usdRows = rows.filter((r) => r.currency === "USD");
  const totalMXN = mxnRows.reduce((sum, r) => sum + Number(r?.amount || 0), 0);
  const totalUSD = usdRows.reduce((sum, r) => sum + Number(r?.amount || 0), 0);
  const hasUSD = usdRows.length > 0;

  return (
    <div style={{ textAlign: "right", fontWeight: 600, paddingTop: 8, display: "flex", justifyContent: "flex-end", gap: 24 }}>
      <span>Total MXN: <span className="expenses-money">$ {totalMXN.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></span>
      {hasUSD && (
        <span>Total USD: <span className="expenses-money">$ {totalUSD.toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></span>
      )}
    </div>
  );
};

const applyPendingTableFilters = (rows = [], activeFilters = {}) => {
  if (!rows.length) {
    return [];
  }

  const normalizedFilters = Object.entries(activeFilters).filter(([, values]) => values && values.length);

  // If the user has explicitly set a status filter, respect it; otherwise default to pending only
  const hasStatusFilter = activeFilters.status && activeFilters.status.length > 0;
  const baseRows = hasStatusFilter ? rows : getPendingRows(rows);

  if (!normalizedFilters.length) {
    return baseRows;
  }

  return baseRows.filter((row) =>
    normalizedFilters.every(([key, values]) => {
      if (!values?.length) {
        return true;
      }

      const selectedValues = values.filter(Boolean);
      if (!selectedValues.length) {
        return true;
      }

      if (key === "fecha_vencimiento" || key === "due_date" || key === "payment_date") {
        const selectedDate = selectedValues[0];
        if (!selectedDate) {
          return true;
        }

        const rowDate = dayjs(row?.[key]);
        const selectedDay = dayjs(selectedDate);
        return rowDate.isValid() && selectedDay.isValid() && rowDate.isSame(selectedDay, "day");
      }

      const rowValue = row?.[key];
      if (rowValue === null || rowValue === undefined) {
        return false;
      }

      return selectedValues.some((value) => {
        const normalizedRowValue = String(rowValue).toLowerCase();
        const normalizedFilterValue = String(value).toLowerCase();

        if (normalizedFilterValue === "pendiente" || normalizedFilterValue === "pagado") {
          return normalizedRowValue === normalizedFilterValue;
        }

        return normalizedRowValue.includes(normalizedFilterValue);
      });
    })
  );
};

const makeDateFilter = () => ({
  filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
    <div style={{ padding: 8 }}>
      <DatePicker
        value={selectedKeys[0] ? dayjs(selectedKeys[0]) : null}
        onChange={(date) => {
          setSelectedKeys(date ? [date.format("YYYY-MM-DD")] : []);
          confirm({ closeDropdown: true });
        }}
        format="DD/MM/YYYY"
        placeholder="Selecciona una fecha"
        style={{ width: 180 }}
      />
      <div style={{ marginTop: 8, display: "flex", justifyContent: "flex-end" }}>
        <Button size="small" onClick={() => { clearFilters?.(); confirm({ closeDropdown: true }); }}>
          Limpiar
        </Button>
      </div>
    </div>
  ),
  filterIcon: (filtered) => (
    <CalendarOutlined style={{ color: filtered ? "#1E3A8A" : undefined, margin: "2px 8px" }} />
  ),
});

const makeSearchFilter = (placeholder = "Buscar...") => ({
  filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
    <div style={{ padding: 8 }}>
      <Input
        placeholder={placeholder}
        value={selectedKeys[0] || ""}
        onChange={(e) => {
          setSelectedKeys(e.target.value ? [e.target.value] : []);
          confirm({ closeDropdown: false });
        }}
        onPressEnter={() => confirm()}
        style={{ width: 220, display: "block", marginBottom: 8 }}
      />
      <Button
        size="small"
        onClick={() => { clearFilters?.(); confirm(); }}
        style={{ width: "100%" }}
      >
        Limpiar
      </Button>
    </div>
  ),
  filterIcon: (filtered) => (
    <SearchOutlined style={{ color: filtered ? "#ffffff" : undefined, padding: "8px 8px" }} />
  ),
});

const getProviderColumns = (handleEditExpense, handleDeleteExpense, tableFilters = {}) => [

  {
    title: "Razón Social",
    dataIndex: "provider",
    key: "provider",
    width: 300,
    filteredValue: tableFilters.provider || null,
    render: (value) => <span className="expenses-cell-main">{value}</span>,
    ...makeSearchFilter("Buscar razón social..."),
  },
  {
    title: "Concepto",
    dataIndex: "concept",
    key: "concept",
    width: 300,
    filteredValue: tableFilters.concept || null,
    ...makeSearchFilter("Buscar concepto..."),
  },
  {
    title: "Folio a Facturar",
    dataIndex: "folio_facturar",
    key: "folio_facturar",
    width: 180,
    filteredValue: tableFilters.folio_facturar || null,
    ...makeSearchFilter("Buscar folio..."),
  },
  {
    title: "Subtotal",
    dataIndex: "subtotal",
    key: "subtotal",
    align: "right",
    width: 210,
    render: (value) => <span className="expenses-money">$ {Number(value || 0).toLocaleString("es-MX")}</span>,
  },
  {
    title: "Ajuste",
    dataIndex: "adjustments",
    key: "adjustments",
    align: "right",
    width: 210,
    render: (value) => <span className="expenses-money">$ {Number(value || 0).toLocaleString("es-MX")}</span>,
  },
  {
    title: "Tasa",
    dataIndex: "tax_rate",
    key: "tax_rate",
    align: "right",
    width: 160,
  },
  {
    title: "Monto",
    dataIndex: "amount",
    key: "amount",
    align: "right",
    width: 220,
    render: (value) => <span className="expenses-money">$ {Number(value || 0).toLocaleString("es-MX")}</span>,
  },
  {
    title: "Moneda",
    dataIndex: "currency",
    key: "currency",
    align: "right",
    width: 160,
    render: (value) => <span className="expenses-money">{value}</span>,
  },
  {
    title: "Fecha  de Pago",
    dataIndex: "fecha_vencimiento",
    key: "fecha_vencimiento",
    align: "right",
    width: 120,
    filteredValue: tableFilters.fecha_vencimiento || null,
    render: (value) => value ? dayjs(value).format("DD/MM/YYYY") : "-",
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => {
      const handleClear = () => {
        setSelectedKeys([]);
        clearFilters?.();
        confirm({ closeDropdown: true });
      };

      return (
        <div style={{ padding: 8 }}>
          <DatePicker
            value={selectedKeys[0] ? dayjs(selectedKeys[0]) : null}
            onChange={(date) => {
              setSelectedKeys(date ? [date.format("YYYY-MM-DD")] : []);
              confirm({ closeDropdown: true });
            }}
            format="DD/MM/YYYY"
            placeholder="Selecciona una fecha"
            style={{ width: 180 }}
          />
          <div style={{ marginTop: 8, display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button size="small" onClick={handleClear}>
              Limpiar
            </Button>
          </div>
        </div>
      );
    },
    filterIcon: (filtered) => <CalendarOutlined style={{ color: filtered ? "#ffffff" : undefined, margin: "2px 8px" }} />,
  },
  {
    title: "Nro. de Orden",
    dataIndex: "numero_orden",
    key: "numero_orden   ",
    align: "right",
    width: 160,
    render: (value) => value || "-",
  },
  {
    title: "Estado",
    dataIndex: "status",
    key: "status",
    align: "center",
    width: 140,
    filteredValue: tableFilters.status || null,
    render: (value) => {
      const color = value === "PENDIENTE" ? "yellow" : value === "PAGADO" ? "green" : "blue";
      return <Tag color={color}>{value}</Tag>;
    },
    filters: [
      { text: "Pendiente", value: "PENDIENTE" },
      { text: "Pagado", value: "PAGADO" },
    ],
  },
  {
    title: "Acciones",
    key: "actions",
    align: "center",
    width: 120,
    render: (_, record) => (
      <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
        <Button type="text" size="small" icon={<EditOutlined />} onClick={() => handleEditExpense(record, "supplier")} />
        <Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={() => handleDeleteExpense(record.id, "supplier")} />
      </div>
    ),
  },
];

const getTaxColumns = (handleEditExpense, handleDeleteExpense, tableFilters = {}) => [

  {
    title: "Impuesto",
    dataIndex: "name",
    key: "name",
    width: 350,
    filteredValue: tableFilters.name || null,
    render: (value) => <span className="expenses-cell-main">{value}</span>,
    ...makeSearchFilter("Buscar impuesto..."),
  },
  {
    title: "Monto",
    dataIndex: "amount",
    key: "amount",
    align: "right",
    width: 350,
    render: (value) => <span className="expenses-money">$ {Number(value || 0).toLocaleString("es-MX")}</span>,
  },
  {
    title: "Fecha de Pago",
    dataIndex: "due_date",
    key: "due_date",
    filteredValue: tableFilters.due_date || null,
    render: (value) => value ? dayjs(value).format("DD/MM/YYYY") : "-",
    ...makeDateFilter(),
  },
  {
    title: "Línea de Captura",
    dataIndex: "capture_line",
    key: "capture_line",
    width: 350,
  },
  {
    title: "Estado",
    dataIndex: "status",
    key: "status",
    align: "center",
    filteredValue: tableFilters.status || null,
    render: (value) => {
      const color = value === "Pendiente" ? "gold" : value === "Pagado" ? "green" : "blue";
      return <Tag color={color}>{value || "Sin estado"}</Tag>;
    },
    filters: [
      { text: "Pendiente", value: "Pendiente" },
      { text: "Pagado", value: "Pagado" },
    ],
  },
  {
    title: "Acciones",
    key: "actions",
    align: "center",
    width: 345,
    render: (_, record) => (
      <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
        <Button type="text" size="small" icon={<EditOutlined />} onClick={() => handleEditExpense(record, "taxes")} />
        <Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={() => handleDeleteExpense(record.id, "taxes")} />
      </div>
    ),
  },
];

const getFixedColumns = (handleEditExpense, handleDeleteExpense, tableFilters = {}) => [

  {
    title: "Nombre",
    dataIndex: "expense_name",
    key: "expense_name",
    width: 450,
    filteredValue: tableFilters.expense_name || null,
    render: (value) => <span className="expenses-cell-main">{value}</span>,
    ...makeSearchFilter("Buscar nombre..."),
  },
  {
    title: "Monto",
    dataIndex: "amount",
    key: "amount",
    align: "right",
    width: 350,
    render: (value) => <span className="expenses-money">$ {Number(value || 0).toLocaleString("es-MX")}</span>,
  },
  {
    title: "Fecha Pago",
    dataIndex: "payment_date",
    key: "payment_date",
    width: 350,
    filteredValue: tableFilters.payment_date || null,
    render: (value) => value ? dayjs(value).format("DD/MM/YYYY") : "-",
    ...makeDateFilter(),
  },
  {
    title: "Estado de Pago",
    dataIndex: "status",
    key: "status",
    align: "center",
    width: 350,
    filteredValue: tableFilters.status || null,
    render: (value) => <Tag color={value === "Pendiente" ? "gold" : value === "Pagado" ? "green" : "blue"}>{value}</Tag>,
    filters: [
      { text: "Pendiente", value: "Pendiente" },
      { text: "Pagado", value: "Pagado" },
    ],
  },

  {
    title: "Acciones",
    key: "actions",
    align: "center",
    width: 150,
    render: (_, record) => (
      <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
        <Button type="text" size="small" icon={<EditOutlined />} onClick={() => handleEditExpense(record, "fixed")} />
        <Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={() => handleDeleteExpense(record.id, "fixed")} />
      </div>
    ),
  },
];

const getVariableColumns = (handleEditExpense, handleDeleteExpense, tableFilters = {}) => [

  {
    title: "Nombre",
    dataIndex: "expense_name",
    key: "expense_name",
    width: 450,
    filteredValue: tableFilters.expense_name || null,
    render: (value) => <span className="expenses-cell-main">{value}</span>,
    ...makeSearchFilter("Buscar nombre..."),
  },
  {
    title: "Monto",
    dataIndex: "amount",
    key: "amount",
    align: "right",
    width: 350,
    render: (value) => <span className="expenses-money">$ {Number(value || 0).toLocaleString("es-MX")}</span>,
  },
  {
    title: "Moneda",
    dataIndex: "currency",
    key: "currency",
    align: "center",
    width: 100,
    render: (value) => <span className="expenses-cell-main">{value}</span>,
  },
  {
    title: "Fecha Pago",
    dataIndex: "payment_date",
    key: "payment_date",
    width: 350,
    filteredValue: tableFilters.payment_date || null,
    render: (value) => value ? dayjs(value).format("DD/MM/YYYY") : "-",
    ...makeDateFilter(),
  },
  {
    title: "Estado de Pago",
    dataIndex: "status",
    key: "status",
    align: "center",
    width: 350,
    filteredValue: tableFilters.status || null,
    render: (value) => <Tag color={value === "Pendiente" ? "gold" : value === "Pagado" ? "green" : "blue"}>{value}</Tag>,
    filters: [
      { text: "Pendiente", value: "Pendiente" },
      { text: "Pagado", value: "Pagado" },
    ],
  },
  {
    title: "Acciones",
    key: "actions",
    align: "center",
    width: 100,
    render: (_, record) => (
      <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
        <Button type="text" size="small" icon={<EditOutlined />} onClick={() => handleEditExpense(record, "variable")} />
        <Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={() => handleDeleteExpense(record.id, "variable")} />
      </div>
    ),
  },
];

const sections = [
  {
    key: "suppliers",
    title: "Proveedores",
    icon: <TeamOutlined />,
    accent: "blue",
    description: "Gastos asociados a proveedores y servicios externos.",
  },
  {
    key: "taxes",
    title: "Impuestos",
    icon: <FileTextOutlined />,
    accent: "indigo",
    description: "Retenciones, impuestos y obligaciones fiscales.",
  },
  {
    key: "fixed",
    title: "Fijo",
    icon: <CalendarOutlined />,
    accent: "violet",
    description: "Gastos recurrentes que se mantienen cada periodo.",
  },
  {
    key: "variable",
    title: "Variable",
    icon: <BarChartOutlined />,
    accent: "cyan",
    description: "Gastos que cambian segÃºn la operaciÃ³n del mes.",
  },
];

const expenseTypeToModal = {
  1: "supplier",
  2: "taxes",
  3: "fixed",
  4: "variable",
};

const sectionKeyToModal = {
  suppliers: "supplier",
  taxes: "taxes",
  fixed: "fixed",
  variable: "variable",
};


const modalMeta = {
  supplier: {
    title: "Cuenta por Pagar",
    kicker: "Nuevo registro",
    successMessage: "Gasto proveedor guardado",
  },
  taxes: {
    title: "Impuestos",
    kicker: "Nuevo registro",
    successMessage: "Gasto de impuestos guardado",
  },
  fixed: {
    title: "Fijo",
    kicker: "Nuevo registro",
    successMessage: "Gasto fijo guardado",
  },
  variable: {
    title: "Variable",
    kicker: "Nuevo registro",
    successMessage: "Gasto variable guardado",
  },
};

const getCurrentUserName = () => {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "null");

    if (!user) {
      return "Admin";
    }

    return (
      user.name ||
      user.full_name ||
      [user.first_name, user.last_name].filter(Boolean).join(" ") ||
      user.email ||
      "Admin"
    );
  } catch {
    return "Admin";
  }
};

const normalizeDateValue = (value) => {
  if (!value) {
    return null;
  }

  if (typeof value?.format === "function") {
    return value.format("YYYY-MM-DD");
  }

  if (typeof value !== "string") {
    return value;
  }

  const trimmedValue = value.trim();

  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmedValue)) {
    return trimmedValue;
  }

  const ddmmyyyyMatch = trimmedValue.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (ddmmyyyyMatch) {
    const [, day, month, year] = ddmmyyyyMatch;
    return `${year}-${month}-${day}`;
  }

  const parsedDate = new Date(trimmedValue);
  if (!Number.isNaN(parsedDate.getTime())) {
    const year = parsedDate.getFullYear();
    const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
    const day = String(parsedDate.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  return trimmedValue;
};

const formatTaxRateValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "8%";
  }

  if (typeof value === "string") {
    return value.includes("%") ? value : `${value}%`;
  }

  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return "8%";
  }

  return numericValue <= 1 ? `${Math.round(numericValue * 100)}%` : `${Math.round(numericValue)}%`;
};



const buildExpensePayload = (modalType, values, selectedPersonType) => {
  const createdBy = getCurrentUserName();
  const personTypeValue = values.persona ?? selectedPersonType;

  if (modalType === "taxes") {
    const taxName = values.nombre_impuesto === "OTRO"
      ? values.nombre_otro_impuesto
      : values.nombre_impuesto;

    return {
      type: "tax",
      person_type: String(personTypeValue),
      created_by: createdBy,
      name: taxName || null,
      due_date: normalizeDateValue(values.fecha_limite),
      payment_date: normalizeDateValue(values.fecha_limite),
      amount: Number(values.monto) || 0,
      capture_line: values.linea_cap || null,
      payment_completed: values.payment_completed_taxes ?? false,
    };
  }

  if (modalType === "fixed") {
    return {
      type: "fixed",
      person_type: String(personTypeValue),
      created_by: createdBy,
      name: values.nombre_gasto_fijo || null,
      amount: Number(values.monto_mxn) || 0,
      payment_completed: values.payment_completed_fixed ?? false,
      payment_date: normalizeDateValue(values.fecha_a_pagar),
    };
  }

  if (modalType === "variable") {
    return {
      type: "variable",
      person_type: String(personTypeValue),
      created_by: createdBy,
      name: values.nombre_gasto_variable || null,
      amount: Number(values.monto) || 0,
      payment_date: normalizeDateValue(values.fecha_a_pagar),
      payment_completed: values.payment_completed_variable ?? false,
      currency: values.currency || "MXN",
    };
  }
  if (modalType === "supplier") {

    return {
      type: "supplier",
      person_type: String(personTypeValue),
      created_by: createdBy,
      business_name: values.razon_social || null,
      concept: values.concepto || null,
      subtotal: Number(values.subtotal) || 0,
      tax_rate: values.tasa || null,
      iva: Number(values.iva) || 0,
      adjustments: values.adjustments ? Number(values.adjustments) : 0,
      amount: Number(values.total) || 0,
      currency: values.moneda || "MXN",
      received_date: normalizeDateValue(values.fecha_recibo),
      invoice_number: values.folio_facturar || null,
      payment_term: values.plazo !== undefined && values.plazo !== null ? Number(values.plazo) : null,
      payment_date: normalizeDateValue(values.fecha_vencimiento),
      due_date: normalizeDateValue(values.fecha_pago_proveedor),
      comment: values.comentario || null,
      interbank_key: values.clave_interbancaria || null,
      purchase_order: values.po || null,
      client: values.cliente || null,
      epno_oc: values.oc_epno || null,
      validation_status: values.validacion_factura || null,
      obligation_status: values.estado_obligacion || null,


    };
  }
};

const mapSupplierExpenseToTable = (expense) => ({
  id: expense.id,
  provider: expense.business_name,
  concept: expense.concept,
  subtotal: expense.subtotal,
  folio_facturar: expense.invoice_number || "-",
  tax_rate: expense.tax_rate,
  fecha_vencimiento: expense.due_date,
  numero_orden: expense.purchase_order,
  adjustments: expense.adjustments || 0,
  description: expense.concept,
  amount: expense.amount,
  currency: expense.currency || "MXN",
  status: expense.obligation_status || "Pendiente",
  raw: expense,
});

const mapTaxExpenseToTable = (expense) => ({
  id: expense.id,
  name: expense.name,
  amount: expense.amount,
  due_date: expense.due_date,
  capture_line: expense.capture_line,
  payment_completed: expense.payment_completed,
  status: expense.payment_completed ? "Pagado" : "Pendiente",
  raw: expense,
});

const mapFixedExpenseToTable = (expense) => ({
  id: expense.id,
  expense_name: expense.name,
  amount: expense.amount,
  payment_completed: expense.payment_completed,
  status: expense.payment_completed ? "Pagado" : "Pendiente",
  payment_date: expense.payment_date,
  raw: expense,
});

const mapVariableExpenseToTable = (expense) => ({
  id: expense.id,
  expense_name: expense.name,
  amount: expense.amount,
  payment_date: expense.payment_date,
  status: expense.payment_completed ? "Pagado" : "Pendiente",
  raw: expense,
  currency: expense.currency || "MXN",
});


const normalizeExpensesForCalendar = (suppliers, taxes, fixed, variable) => {
  const normalized = [];


  suppliers.forEach((expense) => {
    if (expense.due_date) {
      const isPaid =
        expense.payment_completed === true ||
        String(expense.obligation_status || "").toLowerCase() === "pagado";
      normalized.push({
        id: expense.id,
        date: dayjs(expense.due_date),
        amount: expense.amount || 0,
        currency: expense.currency || "MXN",
        provider: expense.business_name,
        concept: expense.concept,
        type: "supplier",
        status: isPaid ? "Pagado" : "Pendiente",
        description: expense.concept,
        raw: expense,
      });
    }
  });

  taxes.forEach((expense) => {
    if (expense.due_date || expense.payment_date) {
      normalized.push({
        id: expense.id,
        date: dayjs(expense.due_date || expense.payment_date),
        amount: expense.amount || 0,
        currency: "MXN",
        provider: expense.name,
        concept: expense.name,
        type: "tax",
        status: expense.payment_completed ? "Pagado" : "Pendiente",
        description: expense.name,
        raw: expense,
      });
    }
  });

  fixed.forEach((expense) => {
    if (expense.payment_date) {
      normalized.push({
        id: expense.id,
        date: dayjs(expense.payment_date),
        amount: expense.amount || 0,
        currency: "MXN",
        provider: expense.name,
        concept: expense.name,
        type: "fixed",
        status: expense.payment_completed ? "Pagado" : "Pendiente",
        description: expense.name,
        raw: expense,
      });
    }
  });


  variable.forEach((expense) => {
    if (expense.payment_date) {
      normalized.push({
        id: expense.id,
        date: dayjs(expense.payment_date),
        amount: expense.amount || 0,
        currency: expense.currency || "MXN",
        provider: expense.name,
        concept: expense.name,
        type: "variable",
        status: expense.payment_completed ? "Pagado" : "Pendiente",
        description: expense.name,
        amountUSD: expense.currency === "USD" ? expense.amount : null,
        raw: expense,
      });
    }
  });

  return normalized;
};

const TIMELINE_EMPTY_CELL = "-";

const getPersonLabel = (personTypeValue) => {
  const numericValue = Number(personTypeValue);
  return personTypes.find((item) => item.value === numericValue)?.label || TIMELINE_EMPTY_CELL;
};

const buildExpensesTimelineRows = (suppliers = [], taxes = [], fixed = [], variable = []) => {
  const rows = [];

  suppliers.forEach((expense) => {
    rows.push({
      fecha: expense.due_date || expense.payment_date || expense.received_date || null,
      servicio: "Proveedor",
      nombre: expense.business_name || TIMELINE_EMPTY_CELL,
      concepto: expense.concept || TIMELINE_EMPTY_CELL,
      subtotal: expense.subtotal ?? TIMELINE_EMPTY_CELL,
      tasa: expense.tax_rate ? formatTaxRateValue(expense.tax_rate) : TIMELINE_EMPTY_CELL,
      iva: expense.iva ?? TIMELINE_EMPTY_CELL,
      ajuste: expense.adjustments ?? TIMELINE_EMPTY_CELL,
      monto: expense.amount ?? 0,
      moneda: expense.currency || "MXN",
      fecha_recibida: expense.received_date || null,
      folio_factura: expense.invoice_number || TIMELINE_EMPTY_CELL,
      plazo: expense.payment_term ?? TIMELINE_EMPTY_CELL,
      fecha_vencimiento: expense.payment_date || null,
      validacion_factura: expense.validation_status || TIMELINE_EMPTY_CELL,
      linea_captura: TIMELINE_EMPTY_CELL,
      estado_pago: expense.obligation_status || "PENDIENTE",
      comentario: expense.comment || TIMELINE_EMPTY_CELL,
      fecha_pago: expense.due_date || null,
      clave_interbancaria: expense.interbank_key || TIMELINE_EMPTY_CELL,
      po: expense.purchase_order || TIMELINE_EMPTY_CELL,
      persona: getPersonLabel(expense.person_type),
      cliente: expense.client || TIMELINE_EMPTY_CELL,
      oc_epno: expense.epno_oc || TIMELINE_EMPTY_CELL,
    });
  });

  taxes.forEach((expense) => {
    rows.push({
      fecha: expense.due_date || expense.payment_date || null,
      servicio: "Impuesto",
      nombre: expense.name || TIMELINE_EMPTY_CELL,
      concepto: expense.name || TIMELINE_EMPTY_CELL,
      subtotal: TIMELINE_EMPTY_CELL,
      tasa: TIMELINE_EMPTY_CELL,
      iva: TIMELINE_EMPTY_CELL,
      ajuste: TIMELINE_EMPTY_CELL,
      monto: expense.amount ?? 0,
      moneda: "MXN",
      fecha_recibida: null,
      folio_factura: TIMELINE_EMPTY_CELL,
      plazo: TIMELINE_EMPTY_CELL,
      fecha_vencimiento: expense.due_date || null,
      validacion_factura: TIMELINE_EMPTY_CELL,
      linea_captura: expense.capture_line || TIMELINE_EMPTY_CELL,
      estado_pago: expense.payment_completed ? "PAGADO" : "PENDIENTE",
      comentario: TIMELINE_EMPTY_CELL,
      fecha_pago: expense.payment_date || null,
      clave_interbancaria: TIMELINE_EMPTY_CELL,
      po: TIMELINE_EMPTY_CELL,
      persona: getPersonLabel(expense.person_type),
      cliente: TIMELINE_EMPTY_CELL,
      oc_epno: TIMELINE_EMPTY_CELL,
    });
  });

  fixed.forEach((expense) => {
    rows.push({
      fecha: expense.payment_date || null,
      servicio: "Fijo",
      nombre: expense.name || TIMELINE_EMPTY_CELL,
      concepto: expense.name || TIMELINE_EMPTY_CELL,
      subtotal: TIMELINE_EMPTY_CELL,
      tasa: TIMELINE_EMPTY_CELL,
      iva: TIMELINE_EMPTY_CELL,
      ajuste: TIMELINE_EMPTY_CELL,
      monto: expense.amount ?? 0,
      moneda: "MXN",
      fecha_recibida: null,
      folio_factura: TIMELINE_EMPTY_CELL,
      plazo: TIMELINE_EMPTY_CELL,
      fecha_vencimiento: expense.payment_date || null,
      validacion_factura: TIMELINE_EMPTY_CELL,
      linea_captura: TIMELINE_EMPTY_CELL,
      estado_pago: expense.payment_completed ? "PAGADO" : "PENDIENTE",
      comentario: TIMELINE_EMPTY_CELL,
      fecha_pago: expense.payment_date || null,
      clave_interbancaria: TIMELINE_EMPTY_CELL,
      po: TIMELINE_EMPTY_CELL,
      persona: getPersonLabel(expense.person_type),
      cliente: TIMELINE_EMPTY_CELL,
      oc_epno: TIMELINE_EMPTY_CELL,
    });
  });

  variable.forEach((expense) => {
    rows.push({
      fecha: expense.payment_date || null,
      servicio: "Variable",
      nombre: expense.name || TIMELINE_EMPTY_CELL,
      concepto: expense.name || TIMELINE_EMPTY_CELL,
      subtotal: TIMELINE_EMPTY_CELL,
      tasa: TIMELINE_EMPTY_CELL,
      iva: TIMELINE_EMPTY_CELL,
      ajuste: TIMELINE_EMPTY_CELL,
      monto: expense.amount ?? 0,
      moneda: expense.currency || "MXN",
      fecha_recibida: null,
      folio_factura: TIMELINE_EMPTY_CELL,
      plazo: TIMELINE_EMPTY_CELL,
      fecha_vencimiento: expense.payment_date || null,
      validacion_factura: TIMELINE_EMPTY_CELL,
      linea_captura: TIMELINE_EMPTY_CELL,
      estado_pago: expense.payment_completed ? "PAGADO" : "PENDIENTE",
      comentario: TIMELINE_EMPTY_CELL,
      fecha_pago: expense.payment_date || null,
      clave_interbancaria: TIMELINE_EMPTY_CELL,
      po: TIMELINE_EMPTY_CELL,
      persona: getPersonLabel(expense.person_type),
      cliente: TIMELINE_EMPTY_CELL,
      oc_epno: TIMELINE_EMPTY_CELL,
    });
  });

  return rows.sort((a, b) => {
    const dateA = a.fecha ? dayjs(a.fecha).valueOf() : Number.POSITIVE_INFINITY;
    const dateB = b.fecha ? dayjs(b.fecha).valueOf() : Number.POSITIVE_INFINITY;
    return dateA - dateB;
  });
};

const TIMELINE_COLUMNS = [
  { header: "Fecha", key: "fecha", width: 14 },
  { header: "Servicio", key: "servicio", width: 14 },
  { header: "Razón Social / Nombre", key: "nombre", width: 30 },
  { header: "Concepto", key: "concepto", width: 30 },
  { header: "Subtotal", key: "subtotal", width: 14 },
  { header: "Tasa", key: "tasa", width: 10 },
  { header: "IVA", key: "iva", width: 14 },
  { header: "Ajuste", key: "ajuste", width: 14 },
  { header: "Monto Total", key: "monto", width: 16 },
  { header: "Moneda", key: "moneda", width: 10 },
  { header: "Fecha Recibida", key: "fecha_recibida", width: 16 },
  { header: "Folio Factura", key: "folio_factura", width: 18 },
  { header: "Plazo", key: "plazo", width: 10 },
  { header: "Fecha de Vencimiento", key: "fecha_vencimiento", width: 18 },
  { header: "Días para Vencer", key: "dias_vencimiento", width: 16 },
  { header: "Validación de Factura", key: "validacion_factura", width: 18 },
  { header: "Línea de Captura", key: "linea_captura", width: 18 },
  { header: "Estado de Pago", key: "estado_pago", width: 16 },
  { header: "Comentario", key: "comentario", width: 26 },
  { header: "Fecha de Pago", key: "fecha_pago", width: 16 },
  { header: "Clave Interbancaria", key: "clave_interbancaria", width: 20 },
  { header: "PO / Nro. Orden", key: "po", width: 16 },
  { header: "Persona", key: "persona", width: 16 },
  { header: "Cliente", key: "cliente", width: 20 },
  { header: "OC EPNO", key: "oc_epno", width: 14 },
];

const TIMELINE_MONEY_COLUMNS = ["subtotal", "iva", "ajuste", "monto"];
const TIMELINE_DATE_COLUMNS = ["fecha", "fecha_recibida", "fecha_vencimiento", "fecha_pago"];

const toExcelDate = (value) => {
  if (!value) {
    return TIMELINE_EMPTY_CELL;
  }

  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.toDate() : TIMELINE_EMPTY_CELL;
};

const exportExpensesTimelineToExcel = async (suppliers, taxes, fixed, variable) => {
  const rows = buildExpensesTimelineRows(suppliers, taxes, fixed, variable);

  if (!rows.length) {
    return false;
  }

  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet("Línea de Tiempo");
  sheet.columns = TIMELINE_COLUMNS;

  rows.forEach((row) => {
    const fechaVencimiento = row.fecha_vencimiento ? dayjs(row.fecha_vencimiento) : null;
    const diasVencimiento = fechaVencimiento && fechaVencimiento.isValid()
      ? fechaVencimiento.startOf("day").diff(dayjs().startOf("day"), "day")
      : TIMELINE_EMPTY_CELL;

    sheet.addRow({
      ...row,
      fecha: toExcelDate(row.fecha),
      fecha_recibida: toExcelDate(row.fecha_recibida),
      fecha_vencimiento: toExcelDate(row.fecha_vencimiento),
      fecha_pago: toExcelDate(row.fecha_pago),
      dias_vencimiento: diasVencimiento,
    });
  });

  const headerRow = sheet.getRow(1);
  headerRow.height = 26;
  headerRow.eachCell((cell) => {
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF1E3A8A" } };
    cell.font = { color: { argb: "FFFFFFFF" }, bold: true };
    cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
    cell.border = {
      top: { style: "thin", color: { argb: "FFD9D9D9" } },
      left: { style: "thin", color: { argb: "FFD9D9D9" } },
      bottom: { style: "thin", color: { argb: "FFD9D9D9" } },
      right: { style: "thin", color: { argb: "FFD9D9D9" } },
    };
  });

  sheet.eachRow((row, rowNumber) => {
    if (rowNumber === 1) {
      return;
    }

    row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      const colKey = TIMELINE_COLUMNS[colNumber - 1]?.key;

      cell.border = {
        top: { style: "thin", color: { argb: "FFE5E7EB" } },
        left: { style: "thin", color: { argb: "FFE5E7EB" } },
        bottom: { style: "thin", color: { argb: "FFE5E7EB" } },
        right: { style: "thin", color: { argb: "FFE5E7EB" } },
      };

      if (rowNumber % 2 === 0) {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF7F9FC" } };
      }

      if (TIMELINE_MONEY_COLUMNS.includes(colKey) && typeof cell.value === "number") {
        cell.numFmt = '"$"#,##0.00';
        cell.alignment = { horizontal: "right" };
      }

      if (TIMELINE_DATE_COLUMNS.includes(colKey) && cell.value instanceof Date) {
        cell.numFmt = "dd/mm/yyyy";
        cell.alignment = { horizontal: "center" };
      }
    });
  });

  sheet.views = [{ state: "frozen", ySplit: 1 }];
  sheet.autoFilter = {
    from: "A1",
    to: `${String.fromCharCode(64 + TIMELINE_COLUMNS.length)}1`,
  };

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `linea_tiempo_gastos_${dayjs().format("YYYYMMDD_HHmm")}.xlsx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);

  return true;
};

function ExpensesList() {
  const [form] = Form.useForm();
  const [dateRangeFilter, setDateRangeFilter] = useState([null, null]);
  const [monthRangeFilter, setMonthRangeFilter] = useState([null, null]);
  const [personType, setPersonType] = useState(1); // Por defecto Persona Moral, pero se puede cambiar
  const [expenseType, setExpenseType] = useState(1);
  const [modalVisible, setModalVisible] = useState(false);
  const [savingExpense, setSavingExpense] = useState(false);
  const [activeExpenseModal, setActiveExpenseModal] = useState("supplier");
  const [allExpenses, setAllExpenses] = useState([]);
  const [expenseSummary, setExpenseSummary] = useState(createEmptyExpenseSummary());
  const [supplierExpenses, setSupplierExpenses] = useState([]);
  const [taxExpenses, setTaxExpenses] = useState([]);
  const [fixedExpenses, setFixedExpenses] = useState([]);
  const [variableExpenses, setVariableExpenses] = useState([]);
  const [loadingExpenses, setLoadingExpenses] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [calendarModalVisible, setCalendarModalVisible] = useState(false);
  const [selectedPaymentDate, setSelectedPaymentDate] = useState(null);
  const [paymentsOnDate, setPaymentsOnDate] = useState([]);
  const [supplierTableFilters, setSupplierTableFilters] = useState({ status: ["Pendiente"] });
  const [taxTableFilters, setTaxTableFilters] = useState({ status: ["Pendiente"] });
  const [fixedTableFilters, setFixedTableFilters] = useState({ status: ["Pendiente"] });
  const [variableTableFilters, setVariableTableFilters] = useState({ status: ["Pendiente"] });
  const watchedSubtotal = Form.useWatch("subtotal", form);
  const watchedTaxRate = Form.useWatch("tasa", form);
  const watchedTaxName = Form.useWatch("nombre_impuesto", form);
  const watchedAjuste = Form.useWatch("ajuste", form);
  const watchedReceivedDate = Form.useWatch("fecha_recibo", form);
  const watchedPaymentTerm = Form.useWatch("plazo", form);
  const watchedDueDate = Form.useWatch("fecha_vencimiento", form);
  const subtotalNumber = Number(watchedSubtotal) || 0;
  const ajusteNumber = Number(watchedAjuste) || 0;
  const taxRateNumber = parseTaxRate(watchedTaxRate);
  const calculatedIva = Number((subtotalNumber * taxRateNumber).toFixed(2));
  const calculatedTotal = Number((subtotalNumber - ajusteNumber + calculatedIva).toFixed(2));

  useEffect(() => {
    if (!modalVisible || activeExpenseModal !== "supplier") {
      return;
    }

    const currentIva = Number(form.getFieldValue("iva")) || 0;
    const currentTotal = Number(form.getFieldValue("total")) || 0;

    if (currentIva !== calculatedIva || currentTotal !== calculatedTotal) {
      form.setFieldsValue({
        iva: calculatedIva,
        total: calculatedTotal,
      });
    }
  }, [activeExpenseModal, calculatedIva, calculatedTotal, form, modalVisible]);

  useEffect(() => {
    if (!modalVisible || activeExpenseModal !== "supplier") {
      return;
    }

    const calculatedDueDate = calculateDueDate(watchedReceivedDate, watchedPaymentTerm);
    const currentDueDate = form.getFieldValue("fecha_vencimiento");
    const currentDueDateValue = currentDueDate ? dayjs(currentDueDate).format("YYYY-MM-DD") : null;
    const nextDueDateValue = calculatedDueDate ? calculatedDueDate.format("YYYY-MM-DD") : null;

    if (currentDueDateValue !== nextDueDateValue) {
      form.setFieldsValue({
        fecha_vencimiento: calculatedDueDate,
      });
    }
  }, [activeExpenseModal, form, modalVisible, watchedPaymentTerm, watchedReceivedDate]);

  useEffect(() => {
    if (!modalVisible || activeExpenseModal !== "supplier") {
      return;
    }

    const calculatedPaymentDate = calculateEstimatedPaymentDate(watchedDueDate);
    const currentPaymentDate = form.getFieldValue("fecha_pago_proveedor");
    const currentPaymentDateValue = currentPaymentDate ? dayjs(currentPaymentDate).format("YYYY-MM-DD") : null;
    const nextPaymentDateValue = calculatedPaymentDate ? calculatedPaymentDate.format("YYYY-MM-DD") : null;

    if (currentPaymentDateValue !== nextPaymentDateValue) {
      form.setFieldsValue({
        fecha_pago_proveedor: calculatedPaymentDate,
      });
    }
  }, [activeExpenseModal, form, modalVisible, watchedDueDate]);

  const applyFiltersToExpenses = (expenses) => {
    const [startDate, endDate] = dateRangeFilter;
    const [startMonth, endMonth] = monthRangeFilter;

    return expenses.filter((expense) => {
      const referenceDate = getExpenseReferenceDate(expense);

      if (startDate || endDate) {
        if (!isDateInRange(referenceDate, startDate, endDate)) {
          return false;
        }
      }

      if (startMonth || endMonth) {
        if (!isMonthInRange(referenceDate, startMonth, endMonth)) {
          return false;
        }
      }

      return true;
    });
  };

  const setExpensesByType = (expenses, apiSummary = null) => {
    const suppliers = expenses.filter(exp => exp.type === "supplier") || [];
    const taxes = expenses.filter(exp => exp.type === "tax") || [];
    const fixed = expenses.filter(exp => exp.type === "fixed") || [];
    const variable = expenses.filter(exp => exp.type === "variable") || [];

    setSupplierExpenses(suppliers);
    setTaxExpenses(taxes);
    setFixedExpenses(fixed);
    setVariableExpenses(variable);

    // Usar el summary del API si está disponible, si no, calcularlo
    if (apiSummary) {
      setExpenseSummary(apiSummary);
    } else {
      setExpenseSummary(buildExpenseSummary(expenses));
    }
  };

  const loadExpenses = async () => {
    try {
      setLoadingExpenses(true);
      const filters = {
        person_type: personType,
      };
      console.log("Cargando gastos con filtros:", filters);

      const response = await ExpensesService.ListExpenses(filters);
      console.log("Respuesta del API de gastos:", response);
      const expenses = response.data || [];
      const apiSummary = response.summary;
      const grandTotal = response.grand_total || 0;


      const summaryWithGrandTotal = apiSummary
        ? {
          grand_total: grandTotal,
          summary: apiSummary,
        }
        : createEmptyExpenseSummary();

      console.log("Gastos cargados:", expenses);
      console.log("Summary del API:", summaryWithGrandTotal);
      setAllExpenses(expenses);

      const filteredExpenses = applyFiltersToExpenses(expenses);
      setExpensesByType(filteredExpenses, summaryWithGrandTotal);
    } catch (error) {
      console.error("Error cargando gastos:", error);
      message.error("No se pudieron cargar los gastos");
      setExpensesByType([], createEmptyExpenseSummary());
    } finally {
      setLoadingExpenses(false);
    }
  };

  useEffect(() => {
    loadExpenses();
  }, [personType]);

  const selectedDateRangeLabel = dateRangeFilter[0] || dateRangeFilter[1]
    ? `${dateRangeFilter[0] ? dayjs(dateRangeFilter[0]).format("DD/MM/YYYY") : "Inicio"} - ${dateRangeFilter[1] ? dayjs(dateRangeFilter[1]).format("DD/MM/YYYY") : "Fin"}`
    : "Sin rango de fechas";

  const selectedMonthRangeLabel = monthRangeFilter[0] || monthRangeFilter[1]
    ? `${monthRangeFilter[0] ? dayjs(monthRangeFilter[0]).format("MMM YYYY") : "Inicio"} - ${monthRangeFilter[1] ? dayjs(monthRangeFilter[1]).format("MMM YYYY") : "Fin"}`
    : "Sin rango de meses";

  const selectedPersonLabel = personTypes.find((type) => type.value === personType)?.label || "";
  const selectedExpenseLabel = expenseTypes.find((type) => type.value === expenseType)?.label || "";

  const getTotalCount = () => {
    const summary = expenseSummary.summary || {};
    return (
      (summary.supplier?.count || 0) +
      (summary.tax?.count || 0) +
      (summary.fixed?.count || 0) +
      (summary.variable?.count || 0)
    );
  };

  const expenseKpiCards = [
    {
      key: "grand_total_mxn",
      title: "Gasto Total general MXN",
      value: expenseSummary.grand_total || 0,
      icon: <DollarOutlined />,
      color: "primary",
      countLabel: `${getTotalCount()} registros`,
    },
    {
      key: "grand_total_usd",
      title: "Gasto Total general USD",
      value: expenseSummary.summary?.supplier?.USD?.total || 0,
      icon: <DollarOutlined />,
      color: "primary",
      countLabel: `${getTotalCount()} registros`,
    },
    {
      key: "supplier_mxn",
      title: "Gastos a Proveedores MXN",
      value: expenseSummary.summary?.supplier?.MXN?.total || 0,
      icon: <TeamOutlined />,
      color: "blue",
    },
    {
      key: "supplier_usd",
      title: "Gastos a Proveedores USD",
      value: expenseSummary.summary?.supplier?.USD?.total || 0,
      icon: <TeamOutlined />,
      color: "blue",
    },
    {
      key: "tax",
      title: "Impuestos",
      value: expenseSummary.summary?.tax?.total || 0,
      icon: <FileTextOutlined />,
      color: "orange",
    },
    {
      key: "fixed",
      title: "Fijos",
      value: expenseSummary.summary?.fixed?.total || 0,
      icon: <CalendarOutlined />,
      color: "green",
    },
    {
      key: "variable_mxn",
      title: "Gastos Variables MXN",
      value: expenseSummary.summary?.variable?.MXN?.total || 0,
      icon: <BarChartOutlined />,
      color: "cyan",
    },
    {
      key: "variable_usd",
      title: "Gastos Variables USD",
      value: expenseSummary.summary?.variable?.USD?.total || 0,
      icon: <BarChartOutlined />,
      color: "cyan",
    },
  ];

  const getPersonDefaults = () => ({
    persona_fisica: personType === 2,
    persona_moral: personType === 1,
  });

  const getModalInitialValues = (modalType) => {
    const personDefaults = getPersonDefaults();

    if (modalType === "taxes") {
      return {
        ...personDefaults,
        nombre_impuesto: "IMSS",
        nombre_otro_impuesto: "",
        fecha_limite: null,
        monto: null,
        linea_cap: "",
      };
    }

    if (modalType === "fixed") {
      return {
        ...personDefaults,
        nombre_gasto_fijo: "",
        monto_mxn: null,
        gasto_mensual: false,
        fecha_a_pagar: null,
      };
    }

    if (modalType === "variable") {
      return {
        ...personDefaults,
        nombre_gasto_variable: "",
        monto: null,
        currency: "MXN",
        fecha_a_pagar: null,
        pago_realizado: false,
      };
    }

    return {
      razon_social: "",
      concepto: "",
      subtotal: null,
      moneda: "MXN",
      tasa: "8%",
      iva: null,
      total: null,
      folio_facturar: "",
      plazo: null,
      fecha_recibo: null,
      fecha_vencimiento: null,
      comentario: "",
      clave_interbancaria: "",
      fecha_pago_proveedor: null,
      persona: personType,
      numero_orden: "",
      cliente: "",
      oc_epno: "",
    };
  };

  const openExpenseModal = (modalType) => {
    const nextModalType = modalType || expenseTypeToModal[expenseType] || "supplier";
    setActiveExpenseModal(nextModalType);
    form.setFieldsValue(getModalInitialValues(nextModalType));
    setModalVisible(true);
  };

  const handleOpenExpense = () => {
    openExpenseModal(expenseTypeToModal[expenseType]);
  };

  const handleCloseModal = () => {
    if (savingExpense) {
      return;
    }

    setModalVisible(false);
    setIsEditMode(false);
    setEditingExpense(null);
    form.resetFields();
  };

  const handleSaveExpense = async () => {
    try {
      setSavingExpense(true);
      const values = await form.validateFields();
      const payload = buildExpensePayload(activeExpenseModal, values, personType);

      const messageKey = "saving-expense";
      message.loading({ content: isEditMode ? "Actualizando..." : "Guardando...", key: messageKey });
      console.log(isEditMode ? "Updating expense..." : "Saving expense...", payload);

      if (isEditMode && editingExpense) {
        await ExpensesService.EditExpense(editingExpense.id, payload);
      } else {
        await ExpensesService.AddExpense(payload);
      }

      message.success({
        content: isEditMode ? "Gasto actualizado correctamente" : (modalMeta[activeExpenseModal]?.successMessage || "Gasto guardado"),
        key: messageKey,
      });
      setModalVisible(false);
      setIsEditMode(false);
      setEditingExpense(null);
      form.resetFields();


      await loadExpenses();
    } catch (error) {
      if (error?.errorFields) {
        return;
      }

      const apiMessage =
        error?.data?.error ||
        error?.response?.data?.error ||
        error?.data?.message ||
        error?.message ||
        "No se pudo guardar el gasto";

      message.error(apiMessage);
    } finally {
      setSavingExpense(false);
    }
  };

  const handleSearch = async () => {
    try {
      setLoadingExpenses(true);
      const filteredExpenses = applyFiltersToExpenses(allExpenses);

      const filteredSummary = buildExpenseSummary(filteredExpenses);
      setExpensesByType(filteredExpenses, filteredSummary);
      message.success("Filtros aplicados");
    } catch (error) {
      console.error("Error aplicando filtros:", error);
      message.error("Error al aplicar los filtros");
    } finally {
      setLoadingExpenses(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      const exported = await exportExpensesTimelineToExcel(
        supplierExpenses,
        taxExpenses,
        fixedExpenses,
        variableExpenses
      );

      if (!exported) {
        message.warning("No hay información para exportar");
        return;
      }

      message.success("Excel exportado correctamente");
    } catch (error) {
      console.error("Error exportando a excel:", error);
      message.error("No se pudo exportar la información a Excel");
    }
  };

  const handleClearFilters = () => {
    setDateRangeFilter([null, null]);
    setMonthRangeFilter([null, null]);
    setPersonType(1);
    const unFilteredSummary = buildExpenseSummary(allExpenses);
    setExpensesByType(allExpenses, unFilteredSummary);
  };

  const handleSectionAdd = (modalType) => (event) => {
    event.stopPropagation();
    openExpenseModal(sectionKeyToModal[modalType] || modalType);
  };

  const handleEditExpense = (expenseData, modalType) => {
    setIsEditMode(true);
    setEditingExpense(expenseData);
    setActiveExpenseModal(modalType);

    const rawExpense = expenseData?.raw || {};

    if (modalType === "supplier") {
      form.setFieldsValue({
        razon_social: rawExpense.business_name || expenseData.provider || "",
        concepto: rawExpense.concept || expenseData.description || "",
        moneda: rawExpense.currency || "MXN",
        subtotal: rawExpense.subtotal ?? expenseData.amount ?? 0,
        adjustments: rawExpense.adjustments ?? 0,
        tasa: formatTaxRateValue(rawExpense.tax_rate),
        iva: rawExpense.iva ?? 0,
        total: rawExpense.amount ?? expenseData.amount ?? 0,
        folio_facturar: rawExpense.invoice_number || expenseData.ruc || "",
        plazo: rawExpense.payment_term ?? null,
        fecha_recibo: rawExpense.received_date ? dayjs(rawExpense.received_date) : null,
        fecha_vencimiento: rawExpense.payment_date ? dayjs(rawExpense.payment_date) : null,
        fecha_pago_proveedor: rawExpense.due_date ? dayjs(rawExpense.due_date) : null,
        comentario: rawExpense.comment || "",
        clave_interbancaria: rawExpense.interbank_key || "",
        po: rawExpense.purchase_order || "",
        cliente: rawExpense.client || "",
        oc_epno: rawExpense.epno_oc || "",
        validacion_factura: rawExpense.validation_status || "S/N",
        estado_obligacion: rawExpense.obligation_status || "PENDIENTE",
      });
    } else if (modalType === "taxes") {
      const rawTaxName = rawExpense.name || expenseData.name || "";
      const normalizedTaxOptions = typeTaxToModal.map((item) => item.value);
      const selectedTaxName = normalizedTaxOptions.includes(rawTaxName) ? rawTaxName : "OTRO";

      form.setFieldsValue({
        nombre_impuesto: selectedTaxName,
        nombre_otro_impuesto: selectedTaxName === "OTRO" ? rawTaxName : "",
        monto: rawExpense.amount ?? expenseData.amount,
        fecha_limite: rawExpense.due_date ? dayjs(rawExpense.due_date) : (expenseData.due_date ? dayjs(expenseData.due_date) : null),
        linea_cap: rawExpense.capture_line || expenseData.capture_line || "",
        payment_completed_taxes: Boolean(rawExpense.payment_completed ?? expenseData.payment_completed),
      });
    } else if (modalType === "fixed") {
      form.setFieldsValue({
        nombre_gasto_fijo: rawExpense.name || expenseData.name || "",
        monto_mxn: rawExpense.amount ?? expenseData.amount ?? 0,
        fecha_a_pagar: rawExpense.payment_date ? dayjs(rawExpense.payment_date) : (expenseData.payment_date ? dayjs(expenseData.payment_date) : null),
        payment_completed_fixed: Boolean(rawExpense.payment_completed ?? expenseData.payment_completed),
      });
    } else if (modalType === "variable") {
      form.setFieldsValue({
        nombre_gasto_variable: rawExpense.name || expenseData.name || "",
        monto: rawExpense.amount ?? expenseData.amount ?? 0,
        fecha_a_pagar: rawExpense.payment_date ? dayjs(rawExpense.payment_date) : (expenseData.payment_date ? dayjs(expenseData.payment_date) : null),
        payment_completed_variable: Boolean(rawExpense.payment_completed ?? expenseData.payment_completed),
        currency: rawExpense.currency || "MXN",
      });
    }

    setModalVisible(true);
  };

  const handleDeleteExpense = async (id, type) => {
    Modal.confirm({
      title: "Eliminar Gasto",
      content: "¿Estás seguro de que deseas eliminar este gasto? Esta acción no se puede deshacer.",
      okText: "Eliminar",
      okType: "danger",
      cancelText: "Cancelar",
      onOk: async () => {
        try {
          const messageKey = "deleting-expense";
          message.loading({ content: "Eliminando...", key: messageKey });
          console.log("Deleting expense with ID:", id);

          await ExpensesService.DeleteExpense(id);
          message.success({
            content: "Gasto eliminado correctamente",
            key: messageKey,
          });
          await loadExpenses();
        } catch (error) {
          console.error("Error eliminando gasto:", error);
          message.error("Error al eliminar el gasto");
        }
      },
    });
  };

  const handleCalendarDateClick = (dateStr, payments) => {
    setSelectedPaymentDate(dateStr);
    setPaymentsOnDate(payments);
    setCalendarModalVisible(true);
  };

  const handleMarkPaymentAsPaid = async (payment) => {
    try {
      const messageKey = "marking-paid";
      message.loading({ content: "Marcando como pagado...", key: messageKey });

      const payload = {
        ...payment.raw,
        payment_completed: true,
        obligation_status: "Pagado",
      };

      await ExpensesService.EditExpense(payment.id, payload);

      message.success({
        content: "Pago marcado como pagado correctamente",
        key: messageKey,
      });

      setPaymentsOnDate((prev) =>
        prev.map((p) => (p.id === payment.id ? { ...p, status: "Pagado" } : p))
      );

      await loadExpenses();
    } catch (error) {
      const apiMessage =
        error?.data?.error ||
        error?.response?.data?.error ||
        error?.data?.message ||
        error?.message ||
        "No se pudo marcar como pagado";

      message.error(apiMessage);
      throw error;
    }
  };

  const toUpperCase = (e) => e.target.value.toUpperCase();

  const renderSupplierModal = () => (
    <>
      <div className="expenses-modal-section">
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Form.Item
              label="Razón Social"
              name="razon_social"
              rules={[{ required: true, message: "Ingresa la razón social" }]}
            >
              <Input uppercase placeholder="Razón social" size="large" onChange={(e) =>
                form.setFieldsValue({
                  razon_social: toUpperCase(e),
                })
              } />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              label="Concepto"
              name="concepto"
              rules={[{ required: true, message: "Ingresa el concepto" }]}
            >
              <Input placeholder="Concepto" size="large" onChange={(e) =>
                form.setFieldsValue({
                  concepto: toUpperCase(e),
                })
              } />
            </Form.Item>
          </Col>


          <Col xs={24} md={8}>
            <Form.Item
              label="Moneda"
              name="moneda"
              rules={[{ required: true, message: "Selecciona la moneda" }]}
            >
              <Select size="large" placeholder="Seleccionar moneda">
                {currencyOptions.map((item) => (
                  <Option key={item.value} value={item.value}>
                    {item.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} md={8}>
            <Form.Item label="Subtotal" name="subtotal" rules={[{ required: true, message: "Ingresa el subtotal" }]}>
              <InputNumber
                style={{ width: "100%" }}
                placeholder="0.00"
                size="large"
                min={0}
                formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label="Ajuste" name="adjustments" rules={[{ required: false, message: "Ingresa el ajuste" }]}>
              <InputNumber
                style={{ width: "100%" }}
                placeholder="0.00"
                size="large"
                min={0}
                formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
              />
            </Form.Item>
          </Col>


          <Col xs={24} md={8}>
            <Form.Item label="Tasa" name="tasa" rules={[{ required: true, message: "Ingresa la tasa" }]}>
              <Select style={{ width: "100%" }} size="large">
                {typeTaxToModal.map((item) => (
                  <Option key={item.value} value={item.value}>
                    {item.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label="IVA" name="iva"  >
              <InputNumber
                style={{ width: "100%" }}
                placeholder="0.00"
                size="large"
                min={0}
                disabled
                formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={8}>
            <Form.Item
              label="Total"
              name="total"
            >
              <InputNumber
                style={{ width: "100%" }}
                placeholder="0.00"
                size="large"
                min={0}
                disabled
                formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
              />
            </Form.Item>
          </Col>

        </Row>
      </div>

      <Divider className="expenses-modal-divider">FACTURACIÓN</Divider>


      <Row gutter={[16, 16]}>
        <Col xs={24} md={8}>
          <Form.Item
            label="Folio Facturar"
            name="folio_facturar"
            rules={[{ required: true, message: "Ingresa el folio" }]}
          >
            <Input size="large" placeholder="Folio" onChange={(e) =>
              form.setFieldsValue({
                folio_facturar: toUpperCase(e),
              })
            } />
          </Form.Item>
        </Col>



        <Col xs={24} md={8}>
          <Form.Item
            label="Fecha Recibo"
            name="fecha_recibo"
            rules={[{ required: true, message: "Ingresa la fecha" }]}
          >
            <DatePicker size="large" style={{ width: "100%" }} format="DD/MM/YYYY" />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item label="Plazo" name="plazo" rules={[{ required: true, message: "Ingresa el plazo" }]}>
            <InputNumber style={{ width: "100%" }} size="large" min={0} placeholder="0" />
          </Form.Item>
        </Col>

        <Col xs={24} md={8}>
          <Form.Item
            label="Clave interbancaria"
            name="clave_interbancaria"

          >
            <Input size="large" placeholder="Clave interbancaria" onChange={(e) =>
              form.setFieldsValue({
                clave_interbancaria: toUpperCase(e),
              })
            } />
          </Form.Item>
        </Col>

        <Col xs={24} md={8}>
          <Form.Item label="Fecha de Vencimiento" name="fecha_vencimiento" >
            <DatePicker size="large" disabled style={{ width: "100%" }} format="DD/MM/YYYY" />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item
            label="Fecha pago proveedor (estimado)"
            name="fecha_pago_proveedor"
            rules={[{ required: true, message: "Ingresa la fecha estimada" }]}
          >
            <DatePicker size="large" style={{ width: "100%" }} format="DD/MM/YYYY" disabled />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item label="Validación de Factura" name="validacion_factura" rules={[{ required: true, message: "Selecciona la validacion de factura" }]}>
            <Select size="large" placeholder="Seleccionar estado">
              {validacionOptions.map((item) => (
                <Option key={item.value} value={item.value}>
                  {item.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item label="Estado de la obligación" name="estado_obligacion" rules={[{ required: true, message: "Selecciona el estado" }]}>
            <Select size="large" placeholder="Seleccionar estado">
              {statusOptions.map((item) => (
                <Option key={item.value} value={item.value}>
                  {item.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item label="Comentario" name="comentario">
            <Input size="large" placeholder="Comentario" onChange={(e) =>
              form.setFieldsValue({
                comentario: toUpperCase(e),
              })
            } />
          </Form.Item>
        </Col>


        <Col xs={24} md={8}>
          <Form.Item label="Numero de Orden" name="po">
            <Input size="large" placeholder="Orden de compra" />
          </Form.Item>
        </Col>

        <Col xs={24} md={8}>
          <Form.Item label="Cliente" name="cliente">
            <Input size="large" placeholder="Cliente" onChange={(e) =>
              form.setFieldsValue({
                cliente: toUpperCase(e),
              })
            } />
          </Form.Item>
        </Col>

        <Col xs={24} md={8}>
          <Form.Item label="OC EPNO" name="oc_epno">
            <Input size="large" placeholder="OC EPNO" onChange={(e) =>
              form.setFieldsValue({
                oc_epno: toUpperCase(e),
              })
            } />
          </Form.Item>
        </Col>

      </Row>
    </>
  );

  const renderTaxesModal = () => (
    <>
      <Row gutter={[16, 16]}>

        <Col xs={24} md={12}>
          <Form.Item label="Nombre" name="nombre_impuesto" rules={[{ required: true, message: "Selecciona el impuesto" }]}>
            <Select size="large" placeholder="Seleccionar impuesto">
              <Option value="IMSS">IMSS</Option>
              <Option value="ISR">ISR</Option>
              <Option value="IVA">IVA</Option>
              <Option value="OTRO">Otro</Option>
            </Select>
          </Form.Item>
        </Col>

        {watchedTaxName === "OTRO" && (
          <Col xs={24} md={12}>
            <Form.Item
              label="Nombre del otro gasto"
              name="nombre_otro_impuesto"
              uppercase
              rules={[{ required: true, message: "Ingresa el nombre del otro gasto" }]}
            >
              <Input size="large" placeholder="Escribe el nombre del impuesto" onChange={(e) =>
                form.setFieldsValue({
                  nombre_otro_impuesto: toUpperCase(e),
                })
              } />
            </Form.Item>
          </Col>
        )}

        <Col xs={24} md={12}>
          <Form.Item
            label="Fecha lí­mite"
            name="fecha_limite"
            rules={[{ required: true, message: "Ingresa la fecha límite" }]}
          >
            <DatePicker size="large" style={{ width: "100%" }} format="DD/MM/YYYY" />
          </Form.Item>
        </Col>

        <Col xs={24} md={12}>
          <Form.Item label="Monto" name="monto" rules={[{ required: true, message: "Ingresa el monto" }]}>
            <InputNumber
              style={{ width: "100%" }}
              size="large"
              min={0}
              placeholder="0.00"
              formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
              parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
            />
          </Form.Item>
        </Col>

        <Col xs={24} md={12}>
          <Form.Item
            label="Línea cap"
            name="linea_cap"
            rules={[{ required: true, message: "Ingresa la línea cap" }]}
          >
            <Input size="large" placeholder="Línea cap" onChange={(e) =>
              form.setFieldsValue({
                linea_cap: toUpperCase(e),
              })
            } />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            label="Estado"
            name="payment_completed_taxes"
            rules={[{ required: true, message: "Selecciona el estado de pago" }]}
          >
            <Select size="large" placeholder="Seleccionar el estado de pago">
              {statusPayOptions.map((item) => (
                <Option key={item.value} value={item.value}>
                  {item.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>
    </>
  );

  const renderFixedModal = () => (
    <>
      <div className="expenses-modal-section">
        <Row gutter={[16, 16]}>

          <Col xs={24} md={12}>
            <Form.Item
              label="Nombre gasto fijo"
              name="nombre_gasto_fijo"
              rules={[{ required: true, message: "Ingresa el nombre del gasto fijo" }]}            >
              <Input size="large" placeholder="Nombre gasto fijo" onChange={(e) =>
                form.setFieldsValue({
                  nombre_gasto_fijo: toUpperCase(e),
                })
              } />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item label="Monto MXN" name="monto_mxn" rules={[{ required: true, message: "Ingresa el monto" }]}>
              <InputNumber
                style={{ width: "100%" }}
                size="large"
                min={0}
                placeholder="0.00"
                formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label="Estado"
              name="payment_completed_fixed"
              rules={[{ required: true, message: "Selecciona el estado de pago" }]}>
              <Select size="large" placeholder="Seleccionar el estado de pago">
                {statusPayOptions.map((item) => (
                  <Option key={item.value} value={item.value}>
                    {item.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>



          <Col xs={24} md={12}>
            <Form.Item
              label="Fecha a pagar"
              name="fecha_a_pagar"
              rules={[{ required: true, message: "Ingresa la fecha a pagar" }]}
            >
              <DatePicker size="large" style={{ width: "100%" }} format="DD/MM/YYYY" />
            </Form.Item>
          </Col>
        </Row>
      </div>
    </>
  );

  const renderVariableModal = () => (
    <>
      <div className="expenses-modal-section">
        <Row gutter={[16, 16]}>

          <Col xs={24} md={12}>
            <Form.Item
              label="Nombre del gasto variable"
              name="nombre_gasto_variable"
              rules={[{ required: true, message: "Ingresa el nombre del gasto variable" }]}
            >
              <Input size="large" placeholder="Nombre del gasto variable" onChange={(e) =>
                form.setFieldsValue({
                  nombre_gasto_variable: toUpperCase(e),
                })
              } />
            </Form.Item>
          </Col>

          <Col xs={24} md={6}>
            <Form.Item label="Monto" name="monto" rules={[{ required: true, message: "Ingresa el monto" }]}>
              <InputNumber
                style={{ width: "100%" }}
                size="large"
                min={0}
                placeholder="0.00"
                formatter={(value) => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item
              label="Moneda"
              name="currency"
              rules={[{ required: true, message: "Selecciona la moneda" }]}
            >
              <Select size="large" placeholder="Seleccionar moneda">
                {currencyOptions.map((item) => (
                  <Option key={item.value} value={item.value}>
                    {item.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              label="Fecha a pagar"
              name="fecha_a_pagar"
              rules={[{ required: true, message: "Ingresa la fecha a pagar" }]}
            >
              <DatePicker size="large" style={{ width: "100%" }} format="DD/MM/YYYY" />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              label="Estado de Pago"
              name="payment_completed_variable"
              rules={[{ required: true, message: "Selecciona el estado de pago" }]}
            >
              <Select size="large" placeholder="Seleccionar el estado de pago">
                {statusPayOptions.map((item) => (
                  <Option key={item.value} value={item.value}>
                    {item.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
      </div>
    </>
  );

  const renderExpenseModal = (modalType) => {
    if (modalType === "taxes") {
      return renderTaxesModal();
    }

    if (modalType === "fixed") {
      return renderFixedModal();
    }

    if (modalType === "variable") {
      return renderVariableModal();
    }

    return renderSupplierModal();
  };

  const renderEmptySection = (meta) => (
    <div className={`expenses-empty-panel expenses-empty-panel-${meta.accent}`}>
      <div className="expenses-empty-hero">
        <div className="expenses-empty-icon">{meta.icon}</div>
        <div className="expenses-empty-copy">
          <Title level={5}>Sin movimientos todaví­a</Title>
          <Text>{meta.description}</Text>
        </div>
      </div>

      <div className="expenses-mini-grid">
        <div className="expenses-mini-card">
          <Text className="expenses-mini-label">Periodo</Text>
          <strong>
            {selectedDateRangeLabel} | {selectedMonthRangeLabel}
          </strong>
        </div>
        <div className="expenses-mini-card">
          <Text className="expenses-mini-label">Tipo de Persona</Text>
          <strong>{selectedPersonLabel}</strong>
        </div>
        <div className="expenses-mini-card">
          <Text className="expenses-mini-label">Tipo de Gasto</Text>
          <strong>{selectedExpenseLabel}</strong>
        </div>
      </div>
    </div>
  );

  const collapseItems = sections.map((meta) => ({
    key: meta.key,
    label: (
      <div className="expenses-collapse-label">
        <span className={`expenses-section-icon expenses-section-icon-${meta.accent}`}>{meta.icon}</span>
        <span>{meta.title}</span>
      </div>
    ),
    extra: (
      <Button
        type="text"
        className="expenses-section-add"
        icon={<PlusOutlined />}
        onClick={handleSectionAdd(meta.key)}
      />
    ),
    children: (
      <div className="expenses-panel-body">
        <div className="expenses-table-card">
          <Table
            columns={
              meta.key === "suppliers" ? getProviderColumns(handleEditExpense, handleDeleteExpense, supplierTableFilters) :
                meta.key === "taxes" ? getTaxColumns(handleEditExpense, handleDeleteExpense, taxTableFilters) :
                  meta.key === "fixed" ? getFixedColumns(handleEditExpense, handleDeleteExpense, fixedTableFilters) :
                    getVariableColumns(handleEditExpense, handleDeleteExpense, variableTableFilters)
            }
            dataSource={(() => {
              const rows =
                meta.key === "suppliers" ? supplierExpenses.map(mapSupplierExpenseToTable) :
                  meta.key === "taxes" ? taxExpenses.map(mapTaxExpenseToTable) :
                    meta.key === "fixed" ? fixedExpenses.map(mapFixedExpenseToTable) :
                      variableExpenses.map(mapVariableExpenseToTable);

              const activeFilters =
                meta.key === "suppliers" ? supplierTableFilters :
                  meta.key === "taxes" ? taxTableFilters :
                    meta.key === "fixed" ? fixedTableFilters :
                      variableTableFilters;

              return applyPendingTableFilters(rows, activeFilters);
            })()}
            rowKey="id"
            pagination={{ pageSize: 20 }}
            scroll={meta.key === "suppliers" ? { x: 1940 } : undefined}
            loading={loadingExpenses}
            onChange={(_, filters) => {
              if (meta.key === "suppliers") {
                setSupplierTableFilters(filters || {});
              } else if (meta.key === "taxes") {
                setTaxTableFilters(filters || {});
              } else if (meta.key === "fixed") {
                setFixedTableFilters(filters || {});
              } else {
                setVariableTableFilters(filters || {});
              }
            }}
            footer={() => {
              const rows =
                meta.key === "suppliers" ? supplierExpenses.map(mapSupplierExpenseToTable) :
                  meta.key === "taxes" ? taxExpenses.map(mapTaxExpenseToTable) :
                    meta.key === "fixed" ? fixedExpenses.map(mapFixedExpenseToTable) :
                      variableExpenses.map(mapVariableExpenseToTable);

              const activeFilters =
                meta.key === "suppliers" ? supplierTableFilters :
                  meta.key === "taxes" ? taxTableFilters :
                    meta.key === "fixed" ? fixedTableFilters :
                      variableTableFilters;

              return renderPendingFooter(applyPendingTableFilters(rows, activeFilters));
            }}
            locale={{
              emptyText: (
                <div className="expenses-table-empty">
                  <div className="expenses-table-empty-icon">
                    <InboxOutlined />
                  </div>
                  <Title level={5}>
                    No hay {meta.key === "suppliers" ? "proveedores" : meta.key} registrados
                  </Title>
                  <Text>Agrega un registro para empezar.</Text>
                </div>
              ),
            }}
          />
        </div>
      </div>
    ),
  }));


  const allNormalizedExpenses = normalizeExpensesForCalendar(
    allExpenses.filter((expense) => expense.type === "supplier"),
    allExpenses.filter((expense) => expense.type === "tax"),
    allExpenses.filter((expense) => expense.type === "fixed"),
    allExpenses.filter((expense) => expense.type === "variable")
  );

  const calendarItem = {
    key: "calendar",
    label: (
      <div className="expenses-collapse-label">
        <span className="expenses-section-icon expenses-section-icon-blue">
          <CalendarOutlined />
        </span>
        <title>Calendario de Pagos</title>
      </div>
    ),
    children: (
      <div className="expenses-panel-body">
        <PaymentCalendar
          allExpenses={allNormalizedExpenses}
          onDateClick={handleCalendarDateClick}
        />
      </div>
    ),
  };

  collapseItems.push(calendarItem);

  return (
    <LayoutPage>
      <div className="expenses-container">
        <Row gutter={[24, 24]}>
          <Col span={24}>
            <Title level={1} style={{ color: "#1E3A8A", fontWeight: 600 }}>
              GASTOS FINANCIEROS
            </Title>
          </Col>

          <Col xs={24} lg={17}>
            <Card className="expenses-shell" bordered={false}>
              <div className="expenses-shell-inner">
                <div className="expenses-shell-header">
                  <div className="expenses-shell-heading">
                    <Title level={4} style={{ color: "#1E3A8A", fontWeight: 700, margin: 0 }}>
                      Cuentas por Pagar
                    </Title>
                  </div>
                </div>

                {/* Filtros Superiores */}
                <div className="expenses-header-actions" style={{ marginTop: 16, marginBottom: 16 }}>
                  <div >
                    <Text className="expenses-field-label">Tipo de Persona</Text>
                    <Select value={personType} onChange={setPersonType} >
                      {personTypes.map((item) => (
                        <Option key={item.value} value={item.value}>
                          {item.label}
                        </Option>
                      ))}
                    </Select>
                  </div>

                  <div >
                    <Text className="expenses-field-label">Tipo de Gasto</Text>
                    <Select value={expenseType} onChange={setExpenseType} >
                      {expenseTypes.map((item) => (
                        <Option key={item.value} value={item.value}>
                          {item.label}
                        </Option>
                      ))}
                    </Select>
                  </div>

                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleOpenExpense}
                    style={{ backgroundColor: "#1E3A8A" }}
                  >
                    {`Agregar ${selectedExpenseLabel}`}
                  </Button>
                </div>

                <Card className="expenses-filter-card" bordered={false}>
                  <div className="expenses-filter-row2">
                    <div>
                      <Text className="expenses-field-label2">Tipo de Persona</Text>
                      <Select value={personType} onChange={setPersonType}>
                        {personTypes.map((item) => (
                          <Option key={item.value} value={item.value}>
                            {item.label}
                          </Option>
                        ))}
                      </Select>
                    </div>
                    <div>
                      <Text className="expenses-field-label2">Fecha</Text>
                      <RangePicker
                        format="DD/MM/YYYY"
                        value={dateRangeFilter[0] || dateRangeFilter[1] ? dateRangeFilter : null}
                        onChange={(dates) => setDateRangeFilter(dates || [null, null])}
                        placeholder={["Fecha inicio", "Fecha fin"]}
                      />
                    </div>
                    <div>
                      <div className="expenses-field expenses-month-picker">
                        <Text className="expenses-field-label2">Mes inicio / Mes fin</Text>
                        <RangePicker
                          picker="month"
                          style={{ width: "100%" }}
                          format="MMM YYYY"
                          separator="→"
                          value={monthRangeFilter[0] || monthRangeFilter[1] ? monthRangeFilter : null}
                          onChange={(dates) => setMonthRangeFilter(dates || [null, null])}
                          placeholder={["Mes inicio", "Mes fin"]}
                        />
                      </div>
                    </div>
                    <div>
                      <Button
                        type="primary"
                        size="large"
                        icon={<ReloadOutlined />}
                        onClick={handleSearch}
                        style={{ backgroundColor: "#1E3A8A", marginRight: 24 }}
                      >
                        Filtrar
                      </Button>
                      <Button
                        size="large"
                        icon={<ClearOutlined />}
                        onClick={handleClearFilters}
                        style={{ marginRight: 24 }}
                      >
                        Limpiar
                      </Button>
                      <Button
                        size="large"
                        icon={<FileExcelOutlined />}
                        onClick={handleExportExcel}
                      >
                        Exportar Excel
                      </Button>
                    </div>
                  </div>
                </Card>

                {/* Estado de Resultados (KPIs) dentro del filtro */}
                <div style={{ marginTop: 24 }}>
                  <div className="expenses-kpi-header">
                    <Text className="expenses-kpi-title">Estado de Resultados</Text>
                  </div>

                  <Row gutter={[10, 10]} className="expenses-kpi-grid" style={{ marginTop: 16 }}>
                    {expenseKpiCards.map((item) => (
                      <Col key={item.key} xs={12} sm={8}>
                        <Card className="ant-card-body" bordered={false} style={{ padding: 0 }} bodyStyle={{ padding: "10px 12px" }}>
                          <div className="kpi-content" style={{ gap: 8 }}>
                            <div className={`expenses-kpi-icon expenses-kpi-${item.color}`} style={{ width: 32, height: 32, minWidth: 32, fontSize: 14 }}>
                              {item.icon}
                            </div>
                            <div className="expenses-kpi-text">
                              <div className="kpi-value" style={{ fontSize: 14, fontWeight: 700 }}>{formatMoney(item.value)}</div>
                              <div className="kpi-title" style={{ fontSize: 11 }}>{item.title}</div>
                            </div>
                          </div>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                </div>
              </div>
            </Card>
          </Col>


          <Col xs={24} lg={7}>
            <Card className="expenses-shell" bordered={false} style={{ height: "100%", padding: 0 }}>
              <div className="expenses-shell-inner" style={{ padding: 0, display: "flex", flexDirection: "column", height: "100%" }}>
                <div className="expenses-panel-body" style={{ flex: 1, display: "flex", flexDirection: "column", padding: 0 }}>
                  <PaymentCalendar
                    allExpenses={allNormalizedExpenses}
                    onDateClick={handleCalendarDateClick}
                  />
                </div>
              </div>
            </Card>
          </Col>

          <Col span={24}>
            <Card className="expenses-shell" bordered={false} style={{ overflow: "visible" }}>
              <div className="expenses-shell-inner">
                <Collapse
                  className="expenses-collapse"
                  ghost
                  defaultActiveKey={["suppliers"]}
                  expandIconPosition="end"
                  expandIcon={({ isActive }) => (
                    <DownOutlined
                      className="expenses-collapse-icon"
                      style={{
                        transform: isActive ? "rotate(180deg)" : "rotate(0deg)",
                      }}
                    />
                  )}
                  items={collapseItems.filter(item => item.key !== 'calendar')}
                />
              </div>
            </Card>
          </Col>
        </Row>

        <Modal
          open={modalVisible}
          onCancel={handleCloseModal}
          footer={null}
          width={980}
          centered
          destroyOnClose
          className="expenses-modal"
          title={
            <Text style={{ color: "#1E3A8A", fontWeight: 600, fontSize: 16 }}>
              {isEditMode ? "Editar" : "Nuevo"} Gasto {modalMeta[activeExpenseModal]?.title}
            </Text>
          }
        >
          <Form form={form} layout="vertical" className="expenses-modal-form" onFinish={handleSaveExpense}>
            {renderExpenseModal(activeExpenseModal)}

            <div className="expenses-modal-actions">
              <Button onClick={handleCloseModal} size="large">
                Cancelar
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                loading={savingExpense}
                style={{ backgroundColor: "#1E3A8A" }}
              >
                Guardar
              </Button>
            </div>
          </Form>
        </Modal>

        <PaymentDetailsModal
          visible={calendarModalVisible}
          onClose={() => setCalendarModalVisible(false)}
          selectedDate={selectedPaymentDate}
          payments={paymentsOnDate}
          onMarkAsPaid={handleMarkPaymentAsPaid}
          loading={loadingExpenses}
        />
      </div>
    </LayoutPage>
  );
}

export default ExpensesList;
